import React from 'react';

export type SplitMode = 'equal' | 'food_alcohol' | 'dietary';

interface SplitModeOption {
  id: SplitMode;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface SplitModeSelectorProps {
  value: SplitMode;
  onChange: (mode: SplitMode) => void;
}

const splitModes: SplitModeOption[] = [
  {
    id: 'equal',
    title: 'Split Equally',
    description: 'Everyone pays the same amount',
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="2" />
        <path d="M8 16h16M16 8v16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'food_alcohol',
    title: 'Food vs Alcohol',
    description: 'Split alcohol only among drinkers',
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <path d="M6 6h8v6l-2 2v12h6v-12l-2-2V6h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="10" cy="26" r="2" fill="currentColor" />
        <circle cx="22" cy="26" r="2" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'dietary',
    title: 'By Dietary Preference',
    description: 'Veg, Non-veg & Alcohol separate',
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none">
        <path d="M16 4L4 12v8l12 8 12-8v-8L16 4z" stroke="currentColor" strokeWidth="2" />
        <path d="M4 12l12 8 12-8" stroke="currentColor" strokeWidth="2" />
        <path d="M16 20v12" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  },
];

export const SplitModeSelector: React.FC<SplitModeSelectorProps> = ({
  value,
  onChange,
}) => {
  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-slate-700">
        How do you want to split?
      </label>
      <div className="grid gap-3">
        {splitModes.map((mode) => (
          <button
            key={mode.id}
            type="button"
            onClick={() => onChange(mode.id)}
            className={`
              flex items-center gap-4 p-4 rounded-2xl border-2 text-left
              transition-all duration-200
              ${value === mode.id
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }
            `}
          >
            <div className={`
              flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
              ${value === mode.id ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}
            `}>
              {mode.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={`font-semibold ${value === mode.id ? 'text-green-700' : 'text-slate-900'}`}>
                {mode.title}
              </h3>
              <p className={`text-sm ${value === mode.id ? 'text-green-600' : 'text-slate-500'}`}>
                {mode.description}
              </p>
            </div>
            <div className={`
              w-6 h-6 rounded-full border-2 flex items-center justify-center
              ${value === mode.id ? 'border-green-500 bg-green-500' : 'border-slate-300'}
            `}>
              {value === mode.id && (
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SplitModeSelector;
